import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Select,
  Table,
  Tabs,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Space,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import apiClient from '@/services/api';

const { Title } = Typography;

interface PlanMonthlyRow {
  month: string;
  planIdx: string;
  planName: string | null;
  issuedCount: number;
  expiredInMonthCount: number;
  suspendedOfIssued: number;
}

interface PlanTotals {
  planIdx: string;
  planName: string | null;
  activeTotal: number;
  suspendedTotal: number;
  expiredTotal: number;
  issuedAllTime: number;
}

export interface VendorMonthlyReport {
  vendorIdx: string;
  vendorName: string | null;
  months: string[];
  monthly: PlanMonthlyRow[];
  totals: PlanTotals[];
}

interface SingleResp {
  Result: string;
  report: VendorMonthlyReport;
}

interface MultiResp {
  Result: string;
  count: number;
  reports: VendorMonthlyReport[];
}

interface Props {
  title: string;
  queryKey: string[];
  reportUrl: string;         // e.g. '/vendor/portal/reports/monthly'
  multi?: boolean;            // if true use .reports[]; else .report
  exportUrl?: string;        // optional export endpoint
  exportFilename?: string;
}

function SingleReport({ report }: { report: VendorMonthlyReport }) {
  // Pivot by plan × month
  const plans = useMemo(() => {
    const map = new Map<string, { planName: string | null; perMonth: Record<string, PlanMonthlyRow> }>();
    for (const m of report.monthly) {
      if (!map.has(m.planIdx)) map.set(m.planIdx, { planName: m.planName, perMonth: {} });
      map.get(m.planIdx)!.perMonth[m.month] = m;
    }
    return Array.from(map.entries()).map(([idx, v]) => ({ idx, name: v.planName, perMonth: v.perMonth }));
  }, [report]);

  const dataSource = plans.map((p) => {
    const row: Record<string, any> = { planIdx: p.idx, planName: p.name ?? '—' };
    for (const m of report.months) {
      const r = p.perMonth[m];
      row[m] = r ? `${r.issuedCount} / ${r.expiredInMonthCount}` : '—';
    }
    return row;
  });

  return (
    <>
      <Row gutter={8} style={{ marginBottom: 16 }}>
        {report.totals.map((t) => (
          <Col key={t.planIdx} span={8} style={{ marginBottom: 8 }}>
            <Card size="small" title={t.planName ?? '—'}>
              <Space size="large">
                <Statistic title="Active" value={t.activeTotal} />
                <Statistic title="Suspended" value={t.suspendedTotal} valueStyle={{ color: '#d46b08' }} />
                <Statistic title="Expired" value={t.expiredTotal} valueStyle={{ color: '#999' }} />
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
      <Card size="small" title="Monthly issuance / expiry (issued / expired)">
        <Table
          size="small"
          rowKey="planIdx"
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: true }}
          columns={[
            { title: 'Plan', dataIndex: 'planName', key: 'planName', fixed: 'left', width: 180 },
            ...report.months.map((m) => ({
              title: m,
              dataIndex: m,
              key: m,
              width: 100,
            })),
          ]}
        />
      </Card>
    </>
  );
}

export default function MonthlyReportView({
  title,
  queryKey,
  reportUrl,
  multi,
  exportUrl,
  exportFilename,
}: Props) {
  const [months, setMonths] = useState(12);

  const { data, isLoading, error } = useQuery({
    queryKey: [...queryKey, months],
    queryFn: async () => {
      const r = await apiClient.get<SingleResp | MultiResp>(reportUrl, { months });
      return r as unknown as SingleResp | MultiResp;
    },
  });

  const reports: VendorMonthlyReport[] = multi
    ? ((data as MultiResp)?.reports ?? [])
    : (data as SingleResp)?.report
      ? [(data as SingleResp).report]
      : [];

  const handleExport = async () => {
    if (!exportUrl) return;
    const resp = await fetch(`/api/v1${exportUrl}?months=${months}`, { credentials: 'include' });
    if (!resp.ok) return;
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFilename ?? `report-${months}m.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          {title}
        </Title>
        <Space>
          <Select
            value={months}
            onChange={setMonths}
            options={[3, 6, 12, 24, 36].map((v) => ({ label: `${v} months`, value: v }))}
            style={{ width: 120 }}
          />
          {exportUrl && (
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Export CSV
            </Button>
          )}
        </Space>
      </Space>

      {error ? (
        <Alert type="error" message="Failed to load report" description={String(error)} />
      ) : isLoading ? (
        <Card loading />
      ) : reports.length === 0 ? (
        <Alert type="info" message="No data" />
      ) : reports.length === 1 ? (
        <SingleReport report={reports[0]} />
      ) : (
        <Tabs
          items={reports.map((r) => ({
            key: r.vendorIdx,
            label: r.vendorName ?? r.vendorIdx,
            children: <SingleReport report={r} />,
          }))}
        />
      )}
    </>
  );
}
